import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CompanyAdminLayout from "../../components/CompanyAdminLayout";
import { expiredFoodsAPI } from "../../lib/companyAdminApi";
import { Clock, RefreshCw, User, ChevronRight, AlertCircle } from "lucide-react";

const PLAN_COLORS = {
  strict:   "bg-blue-50 text-blue-700 border-blue-200",
  moderate: "bg-amber-50 text-amber-700 border-amber-200",
  flexible: "bg-purple-50 text-purple-700 border-purple-200",
};

function PlanBadge({ type, label }) {
  return (
    <span className={"text-[10px] font-semibold px-2 py-0.5 rounded-full border " + (PLAN_COLORS[type] || "bg-muted text-slate-600 border-slate-200")}>
      {label}
    </span>
  );
}

export default function ExpiredFoodsPage() {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await expiredFoodsAPI.get();
      setItems(res.data.items || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const filtered = search.trim()
    ? items.filter(i =>
        i.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
        i.food_name?.toLowerCase().includes(search.toLowerCase()) ||
        i.plan_label?.toLowerCase().includes(search.toLowerCase())
      )
    : items;

  return (
    <CompanyAdminLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <AlertCircle className="w-6 h-6 text-red-500" />
              Expired Food Entries
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              All diet plan food entries that have passed their till date
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text" value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search customer or food..."
              className="border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 w-52"
              data-testid="expired-food-search"
            />
            <button
              onClick={fetch}
              disabled={loading}
              className="p-2 rounded-lg border border-border hover:bg-muted text-muted-foreground disabled:opacity-50"
              data-testid="refresh-expired-btn"
            >
              <RefreshCw className={"w-4 h-4 " + (loading ? "animate-spin" : "")} />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{items.length}</span> total expired entries
          {search && <span>· showing <span className="font-semibold text-foreground">{filtered.length}</span> results</span>}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-20" />
            <p className="font-medium">{search ? "No results found" : "No expired food entries"}</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Customer</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Food / Plan</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Plan Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Expired On</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((item, idx) => (
                  <tr
                    key={item._id || idx}
                    className="hover:bg-muted/20 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate("/admin/customers/" + item.customer_id)}
                        className="flex items-center gap-2 text-left hover:text-primary transition-colors group"
                        data-testid={"expired-customer-" + item.customer_id}
                      >
                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <User className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground group-hover:text-primary truncate max-w-[150px]">{item.customer_name}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[150px]">{item.customer_email}</p>
                        </div>
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground">{item.food_name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <PlanBadge type={item.plan_type} label={item.plan_label} />
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1 text-red-500 text-xs font-medium">
                        <Clock className="w-3 h-3" />
                        {item.till_date ? new Date(item.till_date).toLocaleDateString() : "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate("/admin/customers/" + item.customer_id)}
                        className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-primary"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </CompanyAdminLayout>
  );
}
